/* Phase 10.8A-3 · Catholic Editorial Workspace
 *
 * Digital Archive editorial console — Sources / Queue / Mass Settings.
 * Language discipline (per PRD): the page is called "Catholic Sources",
 * the primary action is "Contribute a Source", never "Upload File".
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./AdminMusicalIntelligenceCanary.css";
import "./AdminCatholicSourcesWorkspace.css";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/admin/musical-intelligence/catholic-sources`;

// ------------------------- helpers -------------------------

async function apiFetch(url, opts = {}) {
  const res = await fetch(url, { credentials: "include", ...opts });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`${res.status} ${res.statusText}${text ? ` — ${text}` : ""}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return v; }
}

function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

const LIFECYCLE_COLORS = {
  Registered: "#5b7f9c",
  Uploaded: "#5b7f9c",
  Parsing: "#8a5a00",
  UnderEditorialReview: "#8a5a00",
  Approved: "#0e7a2c",
  Published: "#0e7a2c",
  Archived: "#666",
  Superseded: "#a63232",
};

// ------------------------- component -------------------------

export default function AdminCatholicSourcesWorkspace() {
  const [tab, setTab] = useState("dashboard");
  const [sources, setSources] = useState([]);
  const [selectedSourceId, setSelectedSourceId] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [queue, setQueue] = useState(null);
  const [queueFilters, setQueueFilters] = useState({
    status: "pending_manual_review",
    confidence_bucket: "",
    search: "",
    page: 1, page_size: 25,
  });
  const [massSettings, setMassSettings] = useState([]);
  const [evidenceDetail, setEvidenceDetail] = useState(null);
  const [contributeOpen, setContributeOpen] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // ------- fetchers -------

  const loadSources = useCallback(async () => {
    try {
      const j = await apiFetch(`${BASE}/sources?limit=100`);
      setSources(j.results || []);
      if (!selectedSourceId && j.results && j.results.length) {
        setSelectedSourceId(j.results[0].id);
      }
    } catch (e) { setError(String(e.message)); }
  }, [selectedSourceId]);

  const loadStatistics = useCallback(async () => {
    try {
      const url = selectedSourceId
        ? `${BASE}/statistics?source_id=${selectedSourceId}`
        : `${BASE}/statistics`;
      const j = await apiFetch(url);
      setStatistics(j);
    } catch (e) { setError(String(e.message)); }
  }, [selectedSourceId]);

  const loadSourceOverview = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const j = await apiFetch(`${BASE}/sources/${sid}`);
      setSelectedSource(j);
    } catch (e) { setError(String(e.message)); }
  }, []);

  const loadQueue = useCallback(async () => {
    if (!selectedSourceId) { setQueue(null); return; }
    try {
      const q = new URLSearchParams();
      if (queueFilters.status) q.set("status", queueFilters.status);
      if (queueFilters.confidence_bucket) q.set("confidence_bucket", queueFilters.confidence_bucket);
      if (queueFilters.search) q.set("search", queueFilters.search);
      q.set("page", String(queueFilters.page));
      q.set("page_size", String(queueFilters.page_size));
      const j = await apiFetch(`${BASE}/sources/${selectedSourceId}/review-queue?${q}`);
      setQueue(j);
    } catch (e) { setError(String(e.message)); }
  }, [selectedSourceId, queueFilters]);

  const loadMassSettings = useCallback(async () => {
    if (!selectedSourceId) { setMassSettings([]); return; }
    try {
      const j = await apiFetch(`${BASE}/mass-settings?source_id=${selectedSourceId}`);
      setMassSettings(j.results || []);
    } catch (e) { setError(String(e.message)); }
  }, [selectedSourceId]);

  useEffect(() => { loadSources(); loadStatistics(); }, [loadSources, loadStatistics]);
  useEffect(() => { if (selectedSourceId) loadSourceOverview(selectedSourceId); },
           [selectedSourceId, loadSourceOverview]);
  useEffect(() => { if (tab === "queue") loadQueue(); }, [tab, loadQueue]);
  useEffect(() => { if (tab === "mass-settings") loadMassSettings(); },
           [tab, loadMassSettings]);

  // ------- actions -------

  const runIngestion = async (mode) => {
    if (!selectedSourceId) return;
    setBusy(true); setError("");
    try {
      const j = await apiFetch(`${BASE}/sources/${selectedSourceId}/${mode}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      alert(`${mode === "dry-run" ? "Dry-run" : "Commit"} complete · ${j.evidence_created || 0} evidence rows · ${j.pages_scanned} pages scanned.`);
      await Promise.all([loadSources(), loadSourceOverview(selectedSourceId), loadStatistics()]);
    } catch (e) { setError(String(e.message)); }
    finally { setBusy(false); }
  };

  const handleReview = async (evidenceId, action, extra = {}) => {
    setBusy(true); setError("");
    try {
      await apiFetch(`${BASE}/review/${evidenceId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, ...extra }),
      });
      await Promise.all([loadQueue(), loadSourceOverview(selectedSourceId), loadStatistics()]);
      setEvidenceDetail(null);
    } catch (e) { setError(String(e.message)); }
    finally { setBusy(false); }
  };

  const handleBulk = async (evidenceIds, action, extra = {}) => {
    if (!evidenceIds.length) return;
    setBusy(true); setError("");
    try {
      await apiFetch(`${BASE}/bulk-review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ evidence_ids: evidenceIds, action, ...extra }),
      });
      await Promise.all([loadQueue(), loadSourceOverview(selectedSourceId), loadStatistics()]);
    } catch (e) { setError(String(e.message)); }
    finally { setBusy(false); }
  };

  const handleMassSettingReview = async (settingId, action) => {
    setBusy(true); setError("");
    try {
      await apiFetch(`${BASE}/mass-settings/${settingId}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      await Promise.all([loadMassSettings(), loadSourceOverview(selectedSourceId)]);
    } catch (e) { setError(String(e.message)); }
    finally { setBusy(false); }
  };

  const openEvidence = async (id) => {
    setError("");
    try {
      const j = await apiFetch(`${BASE}/evidence/${id}`);
      setEvidenceDetail(j);
    } catch (e) { setError(String(e.message)); }
  };

  // ------- render -------

  return (
    <div className="mi-canary catholic-workspace" data-testid="catholic-sources-workspace">
      <header className="mi-canary-header">
        <div>
          <h1 data-testid="workspace-title">Catholic Sources</h1>
          <p className="mi-canary-sub">
            Digital Archive Editorial Workspace · Phase 10.8A-3 · Catholic Musical Intelligence™.
            Contribute new sources, run editorial ingestion, review evidence, and promote canonical works.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button className="mi-run"
                  data-testid="contribute-source-btn"
                  onClick={() => setContributeOpen(true)}>
            + Contribute a Source
          </button>
        </div>
      </header>

      {error && (
        <div className="mi-canary-error" data-testid="workspace-error">{error}</div>
      )}

      {/* Source selector — persists across tabs */}
      <section className="mi-canary-panel" data-testid="source-selector">
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ fontSize: 12, color: "#333" }}>Active source</label>
          <select className="mi-canary-input"
                  data-testid="source-select"
                  value={selectedSourceId || ""}
                  onChange={(e) => setSelectedSourceId(e.target.value)}>
            <option value="">— None —</option>
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {s.source_authority_id} · {s.title}
              </option>
            ))}
          </select>
          {selectedSource?.source && (
            <span data-testid="lifecycle-badge"
                  style={{
                    background: LIFECYCLE_COLORS[selectedSource.source.lifecycle_state] || "#666",
                    color: "white", padding: "3px 10px", borderRadius: 12,
                    fontSize: 12, fontWeight: 600,
                  }}>
              {selectedSource.source.lifecycle_state}
            </span>
          )}
          {selectedSourceId && (
            <>
              <button className="mi-run" disabled={busy}
                      data-testid="dry-run-btn"
                      style={{ background: "#22343f" }}
                      onClick={() => runIngestion("dry-run")}>
                {busy ? "Working…" : "Dry-Run"}
              </button>
              <button className="mi-run" disabled={busy}
                      data-testid="commit-btn"
                      onClick={() => runIngestion("commit")}>
                {busy ? "Working…" : "Commit"}
              </button>
            </>
          )}
        </div>
      </section>

      {/* Tab strip */}
      <section className="mi-canary-panel" data-testid="tab-strip"
               style={{ display: "flex", gap: 8, padding: 6 }}>
        {[
          { id: "dashboard",     label: "Dashboard" },
          { id: "sources",       label: "Sources" },
          { id: "queue",         label: "Review Queue" },
          { id: "mass-settings", label: "Mass Settings" },
        ].map((t) => (
          <button key={t.id}
                  className="mi-run"
                  data-testid={`tab-${t.id}`}
                  style={{
                    background: tab === t.id ? "#c9963a" : "#22343f",
                    padding: "6px 14px", fontSize: 13,
                  }}
                  onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </section>

      {/* Tab bodies */}
      {tab === "dashboard" && (
        <DashboardTab source={selectedSource} statistics={statistics} />
      )}
      {tab === "sources" && (
        <SourcesTab sources={sources} onSelect={setSelectedSourceId}
                    selectedId={selectedSourceId} />
      )}
      {tab === "queue" && (
        <QueueTab queue={queue}
                  filters={queueFilters}
                  setFilters={setQueueFilters}
                  onReload={loadQueue}
                  onReview={handleReview}
                  onBulk={handleBulk}
                  onOpen={openEvidence}
                  busy={busy} />
      )}
      {tab === "mass-settings" && (
        <MassSettingsTab settings={massSettings}
                        onReview={handleMassSettingReview}
                        busy={busy} />
      )}

      {evidenceDetail && (
        <EvidenceDrawer detail={evidenceDetail}
                        onClose={() => setEvidenceDetail(null)}
                        onReview={handleReview}
                        busy={busy} />
      )}

      {contributeOpen && (
        <ContributeDrawer
          onClose={() => setContributeOpen(false)}
          onRegistered={async (source) => {
            setContributeOpen(false);
            await loadSources();
            setSelectedSourceId(source.id);
            await loadSourceOverview(source.id);
          }}
        />
      )}
    </div>
  );
}

// ============================= subcomponents =============================

function DashboardTab({ source, statistics }) {
  const src = source?.source;
  const denom = source?.denomination;
  const coll = source?.collection;
  const history = source?.recent_history || [];

  return (
    <div data-testid="dashboard-tab">
      {src && (
        <section className="mi-canary-panel" data-testid="source-overview-panel">
          <h2 style={{ marginTop: 0 }}>Source Overview</h2>
          <div className="catholic-overview-grid">
            <OverviewCell label="Source Authority Record"
                          value={src.source_authority_id}
                          testid="ov-authority" />
            <OverviewCell label="Denomination"
                          value={denom ? `${denom.name} (${denom.code})` : src.denomination_id}
                          testid="ov-denomination" />
            <OverviewCell label="Collection"
                          value={coll ? `${coll.name} (${coll.code})` : src.collection_id}
                          testid="ov-collection" />
            <OverviewCell label="Resource type" value={src.resource_type}
                          testid="ov-resource-type" />
            <OverviewCell label="Edition" value={src.edition || "—"}
                          testid="ov-edition" />
            <OverviewCell label="Publisher" value={src.publisher || "—"}
                          testid="ov-publisher" />
            <OverviewCell label="Publication year" value={src.publication_year || "—"}
                          testid="ov-year" />
            <OverviewCell label="Lifecycle state" value={src.lifecycle_state}
                          testid="ov-lifecycle" />
            <OverviewCell label="Registered" value={fmtDate(src.created_at)}
                          testid="ov-created" />
            <OverviewCell label="Parser" value={src.parser_name
                            ? `${src.parser_name} v${src.parser_version}` : "not yet run"}
                          testid="ov-parser" />
            <OverviewCell label="Latest batch"
                          value={src.latest_batch_id || "—"}
                          testid="ov-batch" />
            <OverviewCell label="Evidence rows" value={src.evidence_count}
                          testid="ov-evidence" />
            <OverviewCell label="Canonical works created"
                          value={src.canonical_created_count}
                          testid="ov-canonical" />
            <OverviewCell label="Pending review" value={src.pending_review_count}
                          testid="ov-pending" />
            <OverviewCell label="Approved" value={src.approved_count}
                          testid="ov-approved" />
            <OverviewCell label="Rejected" value={src.rejected_count}
                          testid="ov-rejected" />
            <OverviewCell label="Mass settings" value={src.mass_setting_count}
                          testid="ov-mass-settings" />
            <OverviewCell label="SHA-256"
                          value={src.source_sha256
                            ? `${src.source_sha256.slice(0, 16)}…` : "—"}
                          testid="ov-sha" />
          </div>
        </section>
      )}

      {statistics && (
        <section className="mi-canary-panel" data-testid="statistics-panel">
          <h2 style={{ marginTop: 0 }}>Statistics</h2>
          <div className="catholic-stats-grid">
            <StatCard label="Sources" value={statistics.sources} />
            <StatCard label="Evidence" value={statistics.evidence} />
            <StatCard label="Canonical created"
                      value={statistics.canonical_works_created} />
            <StatCard label="Mass settings" value={statistics.mass_settings} />
            <StatCard label="Review progress"
                      value={`${(statistics.review_progress * 100).toFixed(1)}%`} />
            <StatCard label="Remaining queue" value={statistics.remaining_queue} />
            <StatCard label="High-confidence"
                      value={statistics.confidence_distribution?.high_confidence_review} />
            <StatCard label="Manual review"
                      value={statistics.confidence_distribution?.manual_review} />
            <StatCard label="Unmatched"
                      value={statistics.confidence_distribution?.unmatched_provisional} />
          </div>
        </section>
      )}

      {src && (
        <section className="mi-canary-panel" data-testid="processing-history-panel">
          <h2 style={{ marginTop: 0 }}>Processing history</h2>
          {history.length === 0 && <p style={{ color: "#666" }}>No events recorded.</p>}
          <ul className="catholic-timeline" data-testid="history-timeline">
            {history.map((h) => (
              <li key={h.id} data-testid={`history-event-${h.event}`}>
                <span className="catholic-timeline-time">{fmtDate(h.at)}</span>
                <span className="catholic-timeline-event">{h.event}</span>
                <span className="catholic-timeline-actor">
                  {h.actor_email || h.actor_id || "system"}
                </span>
                {h.reason && <span className="catholic-timeline-reason">{h.reason}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function OverviewCell({ label, value, testid }) {
  return (
    <div className="catholic-overview-cell" data-testid={testid}>
      <span>{label}</span>
      <strong>{value ?? "—"}</strong>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="catholic-stat-card">
      <span>{label}</span>
      <strong>{value ?? "—"}</strong>
    </div>
  );
}

function SourcesTab({ sources, onSelect, selectedId }) {
  return (
    <section className="mi-canary-panel" data-testid="sources-tab">
      <h2 style={{ marginTop: 0 }}>Registered sources</h2>
      {sources.length === 0 && (
        <p style={{ color: "#666" }} data-testid="sources-empty">
          No sources yet. Click "Contribute a Source" to register one.
        </p>
      )}
      <table className="catholic-table" data-testid="sources-table">
        <thead>
          <tr>
            <th>Authority ID</th><th>Title</th><th>Collection</th>
            <th>Lifecycle</th><th>Evidence</th><th>Pending</th>
            <th>Approved</th><th>Canonical</th><th>Registered</th>
          </tr>
        </thead>
        <tbody>
          {sources.map((s) => (
            <tr key={s.id}
                data-testid={`source-row-${s.id}`}
                onClick={() => onSelect(s.id)}
                className={selectedId === s.id ? "selected" : ""}>
              <td><code>{s.source_authority_id}</code></td>
              <td>{s.title}</td>
              <td>{s.collection_id}</td>
              <td>
                <span style={{
                  background: LIFECYCLE_COLORS[s.lifecycle_state] || "#666",
                  color: "white", padding: "2px 8px", borderRadius: 10, fontSize: 11,
                }}>
                  {s.lifecycle_state}
                </span>
              </td>
              <td>{s.evidence_count || 0}</td>
              <td>{s.pending_review_count || 0}</td>
              <td>{s.approved_count || 0}</td>
              <td>{s.canonical_created_count || 0}</td>
              <td>{fmtDate(s.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function QueueTab({ queue, filters, setFilters, onReload, onReview, onBulk, onOpen, busy }) {
  const [checked, setChecked] = useState(new Set());
  const results = queue?.results || [];
  const allChecked = results.length && results.every((r) => checked.has(r.id));

  const toggle = (id) => {
    const next = new Set(checked);
    next.has(id) ? next.delete(id) : next.add(id);
    setChecked(next);
  };
  const toggleAll = () => {
    setChecked(allChecked ? new Set() : new Set(results.map((r) => r.id)));
  };

  return (
    <section className="mi-canary-panel" data-testid="queue-tab">
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10,
                     alignItems: "center" }}>
        <input className="mi-canary-input"
               data-testid="queue-search"
               placeholder="Search title/composer/tune…"
               value={filters.search}
               onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })} />
        <select className="mi-canary-input"
                data-testid="queue-status-filter"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}>
          <option value="pending_manual_review">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="deferred">Deferred</option>
          <option value="needs_research">Needs research</option>
        </select>
        <select className="mi-canary-input"
                data-testid="queue-confidence-filter"
                value={filters.confidence_bucket}
                onChange={(e) => setFilters({ ...filters, confidence_bucket: e.target.value, page: 1 })}>
          <option value="">All confidence</option>
          <option value="high">High (≥0.95)</option>
          <option value="manual">Manual (0.80–0.95)</option>
          <option value="low">Low (&lt;0.80)</option>
        </select>
        <button className="mi-run" onClick={onReload} data-testid="queue-refresh-btn">Refresh</button>
      </div>

      {checked.size > 0 && (
        <div className="catholic-bulkbar" data-testid="bulk-toolbar">
          <span>{checked.size} selected</span>
          <button className="mi-run" disabled={busy}
                  onClick={() => onBulk([...checked], "bulk_approve")}
                  data-testid="bulk-approve-btn">Approve</button>
          <button className="mi-run" disabled={busy}
                  style={{ background: "#a63232" }}
                  onClick={() => onBulk([...checked], "bulk_reject")}
                  data-testid="bulk-reject-btn">Reject</button>
          <button className="mi-run" disabled={busy}
                  style={{ background: "#22343f" }}
                  onClick={() => onBulk([...checked], "bulk_defer")}
                  data-testid="bulk-defer-btn">Defer</button>
        </div>
      )}

      <table className="catholic-table" data-testid="queue-table">
        <thead>
          <tr>
            <th><input type="checkbox" checked={!!allChecked}
                       onChange={toggleAll} data-testid="queue-select-all" /></th>
            <th>Title</th><th>Composer</th><th>Page</th>
            <th>Role</th><th>Confidence</th><th>Status</th><th></th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.id} data-testid={`queue-row-${r.id}`}>
              <td><input type="checkbox"
                         checked={checked.has(r.id)}
                         onChange={() => toggle(r.id)} /></td>
              <td>{r.extracted_title}</td>
              <td>{r.composer_name || "—"}</td>
              <td>{r.source_page ?? "—"}</td>
              <td>{r.proposed_rc_role_id || "—"}</td>
              <td>{(r.confidence_score ?? 0).toFixed(2)}</td>
              <td>{r.review_status}</td>
              <td>
                <button className="mi-run" style={{ padding: "3px 10px", fontSize: 12 }}
                        onClick={() => onOpen(r.id)}
                        data-testid={`open-evidence-${r.id}`}>Review</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {queue && (
        <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
          <button className="mi-run" disabled={filters.page <= 1}
                  onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                  data-testid="queue-prev-page">Prev</button>
          <span data-testid="queue-page-indicator">
            Page {queue.page} of {queue.pages || 1} · {queue.total} rows
          </span>
          <button className="mi-run" disabled={filters.page >= (queue.pages || 1)}
                  onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                  data-testid="queue-next-page">Next</button>
        </div>
      )}
    </section>
  );
}

function MassSettingsTab({ settings, onReview, busy }) {
  return (
    <section className="mi-canary-panel" data-testid="mass-settings-tab">
      <h2 style={{ marginTop: 0 }}>Mass settings</h2>
      {settings.length === 0 && (
        <p style={{ color: "#666" }} data-testid="mass-settings-empty">
          No Mass settings detected for this source yet. Run an ingestion to populate.
        </p>
      )}
      <table className="catholic-table" data-testid="mass-settings-table">
        <thead>
          <tr>
            <th>Setting</th><th>Composer</th><th>Language</th>
            <th>Evidence rows</th><th>Status</th><th></th>
          </tr>
        </thead>
        <tbody>
          {settings.map((s) => (
            <tr key={s.id} data-testid={`mass-setting-row-${s.id}`}>
              <td>{s.setting_name}</td>
              <td>{s.composer_name || "—"}</td>
              <td>{s.language || "—"}</td>
              <td>{(s.evidence_ids || []).length}</td>
              <td>{s.review_status}</td>
              <td style={{ display: "flex", gap: 6 }}>
                <button className="mi-run" disabled={busy}
                        style={{ padding: "3px 10px", fontSize: 12 }}
                        onClick={() => onReview(s.id, "mass_setting_approve")}
                        data-testid={`mass-setting-approve-${s.id}`}>Approve</button>
                <button className="mi-run" disabled={busy}
                        style={{ padding: "3px 10px", fontSize: 12,
                                 background: "#a63232" }}
                        onClick={() => onReview(s.id, "mass_setting_reject")}
                        data-testid={`mass-setting-reject-${s.id}`}>Reject</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function EvidenceDrawer({ detail, onClose, onReview, busy }) {
  const ev = detail?.evidence || {};
  const pm = detail?.proposed_match;
  const diff = detail?.side_by_side_diff;
  const history = detail?.history || [];
  const [notes, setNotes] = useState("");

  return (
    <div className="catholic-drawer" data-testid="evidence-detail-drawer">
      <div className="catholic-drawer-inner">
        <header>
          <h2>{ev.extracted_title}</h2>
          <button className="mi-run" onClick={onClose} data-testid="drawer-close">Close</button>
        </header>
        <section>
          <h3>Evidence</h3>
          <ul>
            <li><b>ID</b> <code>{ev.id}</code></li>
            <li><b>Composer</b> {ev.composer_name || "—"}</li>
            <li><b>Tune</b> {ev.tune_name || "—"}</li>
            <li><b>Page</b> {ev.source_page ?? "—"}</li>
            <li><b>Role</b> {ev.proposed_rc_role_id || "—"} ({ev.proposed_rc_role_category || "—"})</li>
            <li><b>Confidence</b> {(ev.confidence_score ?? 0).toFixed(2)}</li>
            <li><b>Status</b> {ev.review_status}</li>
            <li><b>Reasons</b> {(ev.confidence_reasons || []).join(", ") || "—"}</li>
          </ul>
        </section>

        {pm && (
          <section data-testid="proposed-match-section">
            <h3>Proposed canonical match — {pm.title}</h3>
            <p style={{ fontSize: 12, color: "#666" }}>
              Similarity: {detail.proposed_match_similarity ?? "—"}
            </p>
            {diff?.rows?.length > 0 && (
              <table className="catholic-table catholic-diff">
                <thead>
                  <tr>
                    <th>Field</th><th>Canonical</th><th>Evidence</th><th>Similarity</th>
                  </tr>
                </thead>
                <tbody>
                  {diff.rows.map((r) => (
                    <tr key={r.field}
                        className={r.differs ? "differs" : "match"}>
                      <td>{r.field}</td>
                      <td>{r.canonical_value ?? "—"}</td>
                      <td>{r.evidence_value ?? "—"}</td>
                      <td>{r.similarity ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}

        <section>
          <h3>Actions</h3>
          <textarea className="mi-canary-input"
                    placeholder="Reason / notes (optional)"
                    data-testid="review-reason-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    style={{ width: "100%", minHeight: 60 }} />
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <button className="mi-run" disabled={busy}
                    data-testid="approve-btn"
                    onClick={() => onReview(ev.id, "approve", { reason: notes })}>Approve</button>
            <button className="mi-run" disabled={busy}
                    style={{ background: "#a63232" }}
                    data-testid="reject-btn"
                    onClick={() => onReview(ev.id, "reject", { reason: notes })}>Reject</button>
            <button className="mi-run" disabled={busy}
                    style={{ background: "#22343f" }}
                    data-testid="defer-btn"
                    onClick={() => onReview(ev.id, "defer", { reason: notes })}>Defer</button>
            <button className="mi-run" disabled={busy}
                    style={{ background: "#8a5a00" }}
                    data-testid="needs-research-btn"
                    onClick={() => onReview(ev.id, "needs_research", { reason: notes })}>
              Needs research
            </button>
            {pm && (
              <button className="mi-run" disabled={busy}
                      data-testid="merge-into-canonical-btn"
                      onClick={() => onReview(ev.id, "merge_into_canonical", {
                        target_canonical_work_id: pm.id, reason: notes,
                      })}>
                Merge into "{pm.title}"
              </button>
            )}
            <button className="mi-run" disabled={busy}
                    data-testid="create-canonical-btn"
                    onClick={() => onReview(ev.id, "create_new_canonical", {
                      new_canonical_title: ev.extracted_title, reason: notes,
                    })}>
              Create new canonical
            </button>
          </div>
        </section>

        <section>
          <h3>History ({history.length})</h3>
          <ul className="catholic-timeline">
            {history.map((h) => (
              <li key={h.id}>
                <span className="catholic-timeline-time">{fmtDate(h.timestamp)}</span>
                <span className="catholic-timeline-event">{h.action}</span>
                <span className="catholic-timeline-actor">{h.reviewer_email || h.reviewer_id}</span>
                {h.reason && <span className="catholic-timeline-reason">{h.reason}</span>}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

function ContributeDrawer({ onClose, onRegistered }) {
  const [file, setFile] = useState(null);
  const [denominations, setDenominations] = useState([]);
  const [collections, setCollections] = useState([]);
  const [form, setForm] = useState({
    denomination_id: "den_cath",
    collection_id: "col_cath_cbw3",
    resource_type: "hymnal",
    title: "",
    subtitle: "",
    edition: "",
    publisher: "",
    editor: "",
    publication_year: "",
    isbn: "",
    language: "",
    notes: "",
  });
  const [uploaded, setUploaded] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [d, c] = await Promise.all([
          apiFetch(`${API}/api/admin/musical-intelligence/catholic-sources/registry/denominations`).catch(() => null),
          apiFetch(`${API}/api/admin/musical-intelligence/catholic-sources/registry/collections`).catch(() => null),
        ]);
        // Fallback — read directly from the archive collections via
        // an unauthenticated fetch isn't available; leave defaults.
        void d; void c;
      } catch { /* no-op */ }
    })();
  }, []);

  const filteredCollections = useMemo(
    () => collections.filter((c) => c.denomination_id === form.denomination_id),
    [collections, form.denomination_id],
  );

  const upload = async () => {
    if (!file) { setErr("Please choose a PDF."); return; }
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("bucket", "catholic-sources");
      const res = await fetch(`${API}/api/files/upload`, {
        method: "POST", credentials: "include", body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const j = await res.json();
      setUploaded(j);
    } catch (e) { setErr(String(e.message)); }
    finally { setBusy(false); }
  };

  const register = async () => {
    if (!uploaded) { setErr("Upload the PDF first."); return; }
    if (!form.title.trim()) { setErr("Title is required."); return; }
    setBusy(true); setErr("");
    try {
      const payload = { file_upload_id: uploaded.id, ...form };
      // strip empty strings so backend validators don't reject
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
      });
      if (payload.publication_year) payload.publication_year = Number(payload.publication_year);
      const res = await fetch(`${BASE}/sources/register`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload, file_upload_id: uploaded.id }),
      });
      if (!res.ok) throw new Error(await res.text());
      const j = await res.json();
      onRegistered(j.source);
    } catch (e) { setErr(String(e.message)); }
    finally { setBusy(false); }
  };

  return (
    <div className="catholic-drawer" data-testid="contribute-source-drawer">
      <div className="catholic-drawer-inner">
        <header>
          <h2>Contribute a Source</h2>
          <button className="mi-run" onClick={onClose} data-testid="contribute-close">Close</button>
        </header>
        {err && <div className="mi-canary-error" data-testid="contribute-error">{err}</div>}

        <section>
          <h3>Step 1 — Upload the PDF</h3>
          <input type="file" accept="application/pdf"
                 data-testid="contribute-file-input"
                 onChange={(e) => setFile(e.target.files?.[0] || null)} />
          {uploaded ? (
            <p style={{ color: "#0e7a2c" }} data-testid="upload-confirmed">
              Uploaded · {uploaded.id} · {fmtBytes(uploaded.size)}
            </p>
          ) : (
            <button className="mi-run" disabled={!file || busy}
                    style={{ marginLeft: 8 }}
                    onClick={upload}
                    data-testid="upload-file-btn">
              {busy ? "Uploading…" : "Upload"}
            </button>
          )}
        </section>

        <section>
          <h3>Step 2 — Publication metadata</h3>
          <div className="catholic-form-grid">
            <label>Denomination
              <input className="mi-canary-input" value={form.denomination_id}
                     onChange={(e) => setForm({ ...form, denomination_id: e.target.value })}
                     data-testid="form-denomination" />
            </label>
            <label>Collection
              <input className="mi-canary-input" value={form.collection_id}
                     onChange={(e) => setForm({ ...form, collection_id: e.target.value })}
                     data-testid="form-collection" />
            </label>
            <label>Title *
              <input className="mi-canary-input" value={form.title}
                     onChange={(e) => setForm({ ...form, title: e.target.value })}
                     data-testid="form-title" />
            </label>
            <label>Subtitle
              <input className="mi-canary-input" value={form.subtitle}
                     onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                     data-testid="form-subtitle" />
            </label>
            <label>Edition
              <input className="mi-canary-input" value={form.edition}
                     onChange={(e) => setForm({ ...form, edition: e.target.value })}
                     data-testid="form-edition" />
            </label>
            <label>Publisher
              <input className="mi-canary-input" value={form.publisher}
                     onChange={(e) => setForm({ ...form, publisher: e.target.value })}
                     data-testid="form-publisher" />
            </label>
            <label>Publication year
              <input type="number" className="mi-canary-input"
                     value={form.publication_year}
                     onChange={(e) => setForm({ ...form, publication_year: e.target.value })}
                     data-testid="form-year" />
            </label>
            <label>ISBN
              <input className="mi-canary-input" value={form.isbn}
                     onChange={(e) => setForm({ ...form, isbn: e.target.value })}
                     data-testid="form-isbn" />
            </label>
            <label>Language
              <input className="mi-canary-input" value={form.language}
                     onChange={(e) => setForm({ ...form, language: e.target.value })}
                     data-testid="form-language" />
            </label>
          </div>
        </section>

        <section>
          <h3>Step 3 — Register</h3>
          <p style={{ fontSize: 12, color: "#666" }}>
            Registering mints a Source Authority Record and moves the resource to
            "Uploaded". Run "Dry-Run" from the workspace toolbar next to extract
            evidence without persistence; "Commit" persists provisional evidence
            rows for editorial review.
          </p>
          <button className="mi-run" disabled={!uploaded || busy}
                  onClick={register}
                  data-testid="register-source-btn">
            {busy ? "Registering…" : "Register source"}
          </button>
        </section>
      </div>
    </div>
  );
}
